/**
 * The responsive safety net injected into every rendered report or dashboard document.
 *
 * Report and dashboard bodies are AI-authored and routinely apply inline
 * `style="grid-template-columns:repeat(N,1fr)"` to grid containers, often on the
 * very same element that carries a design-system grid class. An inline declaration
 * outranks the class's `@media` breakpoint, so those grids stay multi-column on
 * phones and overflow; bare `<table>`s are clipped by the shell's `overflow-x:hidden`.
 * These `!important` rules override the non-important inline declaration and make
 * EVERY document (including those already generated and served from S3, dark or light)
 * overflow-free without regeneration. Newly generated documents also carry the same
 * rules in their shell template; the duplication is harmless and idempotent.
 *
 * Lifted out of `company/reports/generated-reports/component/view-new-report` on 2026-08-24
 * so the dashboards viewer can apply the identical net rather than copying it (workspace
 * ADR 0021, design section 10). Dashboards need it harder than reports do: widget grids are
 * their main layout primitive, so an un-netted dashboard overflows on nearly every phone.
 * Two copies of a rule tuned against real devices would drift the first time one is fixed.
 */
export const REPORT_RESPONSIVE_NET_CSS = `
@media(max-width:900px){
  [style*="grid-template-columns"]{grid-template-columns:repeat(auto-fit,minmax(min(100%,340px),1fr))!important}
  /* The report's own nav is fixed at the top and grows taller on mobile (hamburger).
     Premium-dark hero content only reserves 64px, so its eyebrow pill collides with the
     nav; reserve enough clearance so the hero always starts below the bar. */
  .hero-content{padding-top:96px!important}
}
@media(max-width:600px){
  [style*="grid-template-columns"]{grid-template-columns:1fr!important}
}
@media screen and (max-width:1024px){
  table{display:block;max-width:100%;overflow-x:auto;-webkit-overflow-scrolling:touch}
}`;

/** The id the injected `<style>` carries, which is also what makes a second call a no-op. */
export const RESPONSIVE_NET_STYLE_ID = 'sai-report-responsive-net';

/**
 * Append the responsive safety-net stylesheet to a generated document so both
 * in-app iframe rendering and the downloaded HTML stay overflow-free on every
 * screen size, regardless of the shell the document was generated with.
 *
 * Idempotent: a document that already carries the style is left untouched, which is what
 * lets a caller run this on every load without stacking duplicate rules.
 */
export function injectResponsiveNet(doc: Document): void {
    const head = doc.head ?? doc.documentElement;
    if (!head || head.querySelector(`#${RESPONSIVE_NET_STYLE_ID}`)) {
        return;
    }
    const style = doc.createElement('style');
    style.id = RESPONSIVE_NET_STYLE_ID;
    style.textContent = REPORT_RESPONSIVE_NET_CSS;
    head.appendChild(style);
}
